import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
interface LightboxProps {
  isOpen: boolean;
  onClose: () => void;
  images: string[];
  currentIndex: number;
  onNavigate: (index: number) => void;
  title?: string;
}
export function Lightbox({
  isOpen,
  onClose,
  images,
  currentIndex,
  onNavigate,
  title
}: LightboxProps) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft' && currentIndex > 0) {
        onNavigate(currentIndex - 1);
      }
      if (e.key === 'ArrowRight' && currentIndex < images.length - 1) {
        onNavigate(currentIndex + 1);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, currentIndex, images.length, onClose, onNavigate]);
  return <AnimatePresence data-id="element-209">
      {isOpen && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center" onClick={onClose} data-id="element-210">
          {/* Close button */}
          <button onClick={onClose} className="absolute top-4 right-4 p-2 text-white hover:bg-white/10 rounded-lg transition-colors z-10" data-id="element-211">
            <X size={32} data-id="element-212" />
          </button>

          {/* Navigation */}
          {currentIndex > 0 && <button onClick={e => {
        e.stopPropagation();
        onNavigate(currentIndex - 1);
      }} className="absolute left-4 p-3 text-white hover:bg-white/10 rounded-lg transition-colors z-10" data-id="element-213">
              <ChevronLeft size={32} data-id="element-214" />
            </button>}

          {currentIndex < images.length - 1 && <button onClick={e => {
        e.stopPropagation();
        onNavigate(currentIndex + 1);
      }} className="absolute right-4 p-3 text-white hover:bg-white/10 rounded-lg transition-colors z-10" data-id="element-215">
              <ChevronRight size={32} data-id="element-216" />
            </button>}

          {/* Image */}
          <motion.div key={currentIndex} initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={{
        opacity: 0,
        scale: 0.9
      }} transition={{
        duration: 0.3
      }} className="max-w-7xl max-h-[90vh] p-4" onClick={e => e.stopPropagation()} data-id="element-217">
            <img src={images[currentIndex]} alt={title || `Image ${currentIndex + 1}`} className="max-w-full max-h-full object-contain rounded-lg" data-id="element-218" />

            {title && <div className="text-center mt-4 text-white text-lg" data-id="element-219">{title}</div>}

            <div className="text-center mt-2 text-white/60 text-sm" data-id="element-220">
              {currentIndex + 1} / {images.length}
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}